import * as Yup from 'yup';
import { optionValidationSchema } from './optionSchema';
import { isContentLocaleEnabled } from '@/lib/platform-filters';

/**
 * Create dynamic question validation schema
 *
 * @returns Yup validation schema
 *
 * NOTE: Sport validation currently commented out because questions don't have
 * a sport field in the database. This validation is prepared for future enhancement
 * when the data model is updated to include question-level sport tracking.
 *
 * FUTURE: This function can accept a pickem parameter when implementing sport validation:
 * ```typescript
 * interface Pickem {
 *   id: string;
 *   sport: 'NFL' | 'NBA' | 'NHL' | 'Mixed';
 * }
 * export const createQuestionValidationSchema = (pickem?: Pickem | null) => {
 *   // Validate sport matches pickem if provided
 * }
 * ```
 */
export const createQuestionValidationSchema = () => {
  return Yup.object({
    pickem_id: Yup.string().required('Pickem is required'),

    // FUTURE: Sport validation when question model includes sport field
    // This would validate that question sport matches pickem sport (unless Mixed)
    // sport: Yup.string().test(
    //   'sport-matches-pickem',
    //   'Question sport must match pickem sport',
    //   function(value) {
    //     if (!_pickem || _pickem.sport === 'Mixed' || !value) return true;
    //     return value === _pickem.sport;
    //   }
    // ),
    question_text_en: Yup.string()
      .required('English question text is required')
      .min(5, 'English question must be at least 5 characters')
      .max(500, 'English question must be less than 500 characters'),
    /**
     * Required only where the instance declares this Content Locale (CAE-762): the form
     * renders no box for it otherwise, so requiring it would block every save behind an
     * error naming a field the Game Manager cannot see.
     *
     * `.test` rather than a conditional chain, so the declaration is read per validation
     * rather than at import — see the note on `option_text_fr`.
     */
    question_text_fr: Yup.string()
      .nullable()
      .max(500, 'Spanish question must be less than 500 characters')
      .test('content-locale-required', 'Spanish question text is required', value =>
        isContentLocaleEnabled('fr') ? Boolean(value) : true
      )
      .test('content-locale-min', 'Spanish question must be at least 5 characters', value =>
        isContentLocaleEnabled('fr') && value ? value.length >= 5 : true
      ),
    question_subtext_en: Yup.string()
      .max(500, 'Subtext must be less than 500 characters')
      .nullable(),
    question_subtext_fr: Yup.string()
      .max(500, 'Subtext must be less than 500 characters')
      .nullable(),
    question_image_en: Yup.string().url('Must be a valid URL').nullable(),
    question_image_fr: Yup.string().url('Must be a valid URL').nullable(),
    start_utc: Yup.string().nullable(),
    question_type: Yup.string().required('Question type is required'),
    points: Yup.number().min(0, 'Points must be 0 or greater').required('Points are required'),
    stat_type_code: Yup.string()
      .nullable()
      .test(
        'stat-type-required-for-over-under-with-fixture',
        'Stat type is required when a fixture is selected for over/under questions',
        function (value) {
          const { question_type, fixture_external_id } = this.parent;
          const isOverUnder =
            question_type === 'player_over_under' || question_type === 'team_over_under';
          if (!isOverUnder || !fixture_external_id) {
            return true;
          }
          return !!(value && value.trim());
        }
      ),
    options: Yup.array()
      .of(optionValidationSchema)
      .when('question_type', {
        is: (type: string) => type === 'tiebreaker',
        then: schema => schema.notRequired(),
        otherwise: schema =>
          schema
            .required('Options are required')
            .test('min-options-by-type', '', function (options) {
              const questionType = this.parent.question_type;
              const count = options?.length ?? 0;
              if (questionType === 'custom' && count < 1) {
                return this.createError({
                  path: this.path,
                  message: 'At least 1 option is required for custom questions',
                });
              }
              if (questionType !== 'custom' && count < 2) {
                return this.createError({
                  path: this.path,
                  message: 'At least 2 options are required',
                });
              }
              return true;
            })
            .test(
              'max-one-correct',
              'No more than one option can be marked as correct',
              function (options) {
                if (!options) return false;
                const correctCount = options.filter(opt => opt.is_correct).length;
                return correctCount <= 1;
              }
            ),
      })
      .when('question_type', {
        is: (type: string) =>
          type === 'over_under' || type === 'player_over_under' || type === 'team_over_under',
        then: schema =>
          schema.test(
            'over-under-exactly-two',
            'Over/Under questions must have exactly 2 options',
            function (options) {
              return options?.length === 2;
            }
          ),
      })
      .test(
        'player-grading-validation',
        'Player selection required for player-based questions',
        function (options) {
          const { question_metadata, sportcc_match_id } = this.parent;

          // Only validate if SportCC match is linked
          if (!sportcc_match_id || !question_metadata?.grading_type) {
            return true;
          }

          const gradingType = question_metadata.grading_type;
          const requiresPlayer = [
            'player_goals_over_under',
            'player_assists_over_under',
            'player_points_over_under',
            'grouped_player_most_points',
          ].includes(gradingType);

          if (!requiresPlayer || !options) {
            return true;
          }

          // Check each option has player_id
          const invalidOptions = options.filter((opt: any) => !opt.grading_data?.playerId);

          if (invalidOptions.length > 0) {
            return this.createError({
              path: `options[${options.indexOf(invalidOptions[0])}].grading_data.playerId`,
              message: 'Player selection is required for this grading type',
            });
          }

          return true;
        }
      )
      .test(
        'question-type-entity-match',
        'Option entity type must match question type',
        function (options) {
          const questionType = this.parent.question_type;

          if (!options || !questionType || questionType === 'custom') {
            return true;
          }

          // Validate team questions
          if (questionType === 'team' || questionType === 'team_over_under') {
            for (let i = 0; i < options.length; i++) {
              const opt = options[i];
              const playerId = opt.grading_data?.playerId;

              if (playerId) {
                return this.createError({
                  path: `options[${i}].grading_data.playerId`,
                  message:
                    'Team questions cannot have player selections. Please select a team instead.',
                });
              }
            }
          }

          // Validate player questions
          if (questionType === 'player' || questionType === 'player_over_under') {
            for (let i = 0; i < options.length; i++) {
              const opt = options[i];
              const teamId = opt.grading_data?.teamId;
              const playerId = opt.grading_data?.playerId;

              if (teamId && !playerId) {
                return this.createError({
                  path: `options[${i}].grading_data.playerId`,
                  message:
                    'Player questions require player selections. Please select a player, not just a team.',
                });
              }
            }
          }

          return true;
        }
      ),
    question_metadata: Yup.object()
      .shape({
        grading_type: Yup.string().nullable(),
        grading: Yup.object()
          .shape({
            type: Yup.string().nullable(),
            config: Yup.object().shape({
              threshold: Yup.number().nullable(),
              comparison: Yup.string().oneOf(['over', 'under']).nullable(),
              statType: Yup.string().nullable(),
              playerId: Yup.string().nullable(),
              playerName: Yup.string().nullable(),
              playerPosition: Yup.string().nullable(),
            }),
          })
          .nullable(),
        config: Yup.object()
          .shape({
            threshold: Yup.number().nullable(),
            comparison: Yup.string().oneOf(['over', 'under']).nullable(),
          })
          .nullable(),
      })
      .nullable()
      .test(
        'over-under-config-validation',
        'Over/Under configuration is incomplete',
        function (metadata) {
          const questionType = this.parent.question_type;

          // Only validate for over_under questions
          if (questionType !== 'over_under') {
            return true;
          }

          const config = metadata?.grading?.config;

          if (!config) {
            return this.createError({
              path: 'question_metadata.grading.config',
              message:
                'Over/Under configuration is required. Please select a player and configure the stat threshold.',
            });
          }

          // Validate threshold - return specific error with field path
          if (!config.threshold && config.threshold !== 0) {
            return this.createError({
              path: 'question_metadata.grading.config.threshold',
              message: 'Stat threshold is required. Enter the target value for the over/under.',
            });
          } else if (config.threshold < 0) {
            return this.createError({
              path: 'question_metadata.grading.config.threshold',
              message: 'Threshold must be 0 or greater.',
            });
          }

          // Validate statType
          if (!config.statType) {
            return this.createError({
              path: 'question_metadata.grading.config.statType',
              message:
                'Stat type is required. Select the statistic to track (e.g., passing yards, touchdowns).',
            });
          } else {
            const validStatTypes = [
              'rushing_yards',
              'passing_yards',
              'receptions',
              'receiving_yards',
              'touchdowns',
              'points',
              'assists',
              'rebounds',
              'goals',
            ];
            if (!validStatTypes.includes(config.statType)) {
              return this.createError({
                path: 'question_metadata.grading.config.statType',
                message: `Invalid stat type "${config.statType}". Must be one of: ${validStatTypes.join(', ')}.`,
              });
            }
          }

          // Validate playerId
          if (!config.playerId) {
            return this.createError({
              path: 'question_metadata.grading.config.playerId',
              message: 'Player ID is required. Use the Question Coach to select a player.',
            });
          }

          // Validate playerName
          if (!config.playerName) {
            return this.createError({
              path: 'question_metadata.grading.config.playerName',
              message: 'Player name is required. Use the Question Coach to select a player.',
            });
          }

          // Validate playerPosition
          if (!config.playerPosition) {
            return this.createError({
              path: 'question_metadata.grading.config.playerPosition',
              message: 'Player position is required. Use the Question Coach to select a player.',
            });
          }

          return true;
        }
      ),
  });
};

/**
 * Legacy static validation schema - deprecated
 * @deprecated Use createQuestionValidationSchema() for proper validation
 *
 * This export is kept for backwards compatibility with existing code that doesn't pass pickem data.
 * New code should use createQuestionValidationSchema(pickem) instead.
 */
export const questionValidationSchema = createQuestionValidationSchema();
